import {
  BALANCE,
  BUILDINGS,
  BUILDING_IDS,
  CLOUD_LEVELS,
  INVASION,
  LEVERS,
  LEVER_IDS,
  RESEARCH,
  SHIP_CLASSES,
  SHIP_CLASS_IDS,
  TECHS,
  TECH_IDS,
  type BuildingId,
  type CloudLevelId,
  type TechBranchId,
  type TechId,
  type TechTier,
  type TradeGoodId,
} from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import {
  commandBlocker,
  researchSlots,
  techPrice,
  type Command,
  type GameState,
  type PlayerState,
} from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num } from './format';
import { reasonText } from './planetModel';

/**
 * The «Research» tab (#58): the tree by branch and tier, what each
 * technology does and opens, its price after diffusion and fragments, and
 * the commands to start or cancel it. Pure: state in, words out.
 */
export type TechStatus = 'known' | 'active' | 'open' | 'locked';

export interface TechNode {
  readonly id: TechId;
  readonly name: string;
  readonly branch: TechBranchId;
  readonly tier: TechTier;
  readonly status: TechStatus;
  /** Science price now, and how much is already in. */
  readonly price: number;
  readonly progress: number;
  /** Exotics still to pay on the first start (0 once paid or for tiers 1–2). */
  readonly exotics: number;
  /** Shares off the base price: other players knowing it, fragments found. */
  readonly knownBy: number;
  readonly discount: number;
  /** One line on the card. */
  readonly note: string;
  /** What it does and what it opens, for the detail. */
  readonly does: readonly string[];
  readonly requires: string;
  readonly command: Command | null;
  readonly reason: string | null;
}

export interface ResearchView {
  readonly slots: { readonly used: number; readonly total: number };
  readonly perCycle: number;
  readonly stock: number;
  readonly branches: readonly { readonly id: TechBranchId; readonly name: string }[];
  readonly nodes: readonly TechNode[];
  /** Research running now, in order, with a rough time to finish. */
  readonly active: readonly {
    readonly id: TechId;
    readonly name: string;
    /** Share done, 0..1 (#115). */
    readonly value: number;
    readonly eta: string;
  }[];
  readonly hint: string;
}

const BRANCHES: readonly TechBranchId[] = [
  'industry',
  'trade',
  'exploration',
  'fleet',
  'resilience',
];
const pct = (x: number) => Math.round(x * 100);

const list = (locale: Locale, ids: readonly BuildingId[]) =>
  ids.map((b) => t(locale, `building.${b}.name`)).join(', ');

/** What a technology changes, one line per effect, from its numbers in content. */
export function techEffects(locale: Locale, id: TechId): string[] {
  const e = TECHS[id].effects;
  const out: string[] = [];
  const jobs = Object.entries(e.jobsPerLevel ?? {}) as [BuildingId, number][];
  if (jobs.length) {
    const n = jobs[0]![1];
    out.push(
      t(locale, 'research.fx.jobs', {
        n: `${n > 0 ? '+' : '−'}${Math.abs(n)}`,
        list: list(
          locale,
          jobs.map(([b]) => b),
        ),
      }),
    );
  }
  const output = Object.entries(e.buildingOutput ?? {}) as [BuildingId, number][];
  for (const share of [...new Set(output.map(([, n]) => n))]) {
    const ids = output.filter(([, n]) => n === share).map(([b]) => b);
    out.push(t(locale, 'research.fx.output', { pct: pct(share), list: list(locale, ids) }));
  }
  for (const [b, goods] of Object.entries(e.buildingInputs ?? {}) as [
    BuildingId,
    Partial<Record<TradeGoodId, number>>,
  ][]) {
    for (const [g, f] of Object.entries(goods) as [TradeGoodId, number][]) {
      out.push(
        t(locale, 'research.fx.inputs', {
          good: t(locale, `resource.${g}`).toLowerCase(),
          pct: pct(1 - f),
          building: t(locale, `building.${b}.name`),
        }),
      );
    }
  }
  if (e.heliumPerEnergy)
    out.push(t(locale, 'research.fx.helium', { pct: pct(1 - e.heliumPerEnergy) }));
  if (e.orbitSlots) out.push(t(locale, 'research.fx.orbitSlots', { n: e.orbitSlots }));
  if (e.surfaceSlots) out.push(t(locale, 'research.fx.surfaceSlots', { n: e.surfaceSlots }));
  if (e.populationGrowth)
    out.push(t(locale, 'research.fx.growth', { pct: pct(e.populationGrowth) }));
  if (e.outpostOutput) out.push(t(locale, 'research.fx.outposts', { pct: pct(e.outpostOutput) }));
  if (e.exoticsYield)
    out.push(t(locale, 'research.fx.exotics', { x: num(locale, e.exoticsYield, 1) }));
  if (e.contractSlots) out.push(t(locale, 'research.fx.contracts', { n: e.contractSlots }));
  if (e.caravanSpeed) out.push(t(locale, 'research.fx.caravans', { pct: pct(e.caravanSpeed) }));
  if (e.conversionFee !== undefined)
    out.push(t(locale, 'research.fx.fee', { pct: num(locale, e.conversionFee * 100) }));
  if (e.seigniorage)
    out.push(t(locale, 'research.fx.seigniorage', { pct: num(locale, e.seigniorage * 100) }));
  if (e.scanTime) out.push(t(locale, 'research.fx.scan', { pct: pct(1 - e.scanTime) }));
  if (e.fleetUpkeep) out.push(t(locale, 'research.fx.upkeep', { pct: pct(1 - e.fleetUpkeep) }));
  if (e.fleetLimit) out.push(t(locale, 'research.fx.fleetLimit', { n: e.fleetLimit }));
  if (e.crisisWarningTicks)
    out.push(t(locale, 'research.fx.warning', { time: clock(e.crisisWarningTicks) }));
  if (e.crisisDamage)
    out.push(t(locale, 'research.fx.crisisDamage', { pct: pct(1 - e.crisisDamage) }));
  if (e.projectPrestige)
    out.push(t(locale, 'research.fx.projects', { pct: pct(e.projectPrestige) }));
  if (e.megastructure) out.push(t(locale, 'research.fx.megastructure'));
  if (e.revealMap) out.push(t(locale, 'research.fx.revealMap'));
  if (e.autoTrade) out.push(t(locale, 'research.fx.autoTrade'));
  if (e.formations) out.push(t(locale, 'research.fx.formations'));
  return out;
}

/** What content elsewhere waits for this technology: buildings, ships, clouds, levers, landings. */
export function techUnlocks(locale: Locale, id: TechId): string[] {
  const out: string[] = [];
  for (const b of BUILDING_IDS)
    if (BUILDINGS[b].requiresTech === id) out.push(t(locale, `building.${b}.name`));
  for (const s of SHIP_CLASS_IDS)
    if (SHIP_CLASSES[s].requiresTech === id) out.push(t(locale, `ship.${s}.name`));
  for (const level of [1, 2, 3] as CloudLevelId[])
    if (CLOUD_LEVELS[level].requiresTech === id)
      out.push(t(locale, 'research.unlock.cloud', { level }));
  for (const l of LEVER_IDS)
    if (LEVERS[l].requiresTech === id) out.push(t(locale, `lever.${l}.name`));
  if (INVASION.requiresTech === id) out.push(t(locale, 'research.unlock.invasion'));
  return out;
}

export function researchView(state: GameState, v: Viewer): ResearchView | null {
  const { locale, me } = v;
  const player = state.players.find((p) => p.id === me);
  if (!player) return null;
  const r = player.research;
  const perCycle = player.planet.lastCycle?.science ?? 0;
  const stock = player.planet.science;
  const knowers = (tech: TechId) =>
    state.players.filter((p) => p.id !== me && p.techs.includes(tech)).length;

  const nodes = TECH_IDS.map((id): TechNode => {
    const def = TECHS[id];
    const known = player.techs.includes(id);
    const active = r.active.includes(id);
    const met = def.requires.filter((x) => player.techs.includes(x)).length >= def.requiresCount;
    const status: TechStatus = known ? 'known' : active ? 'active' : met ? 'open' : 'locked';
    const knownBy = knowers(id);
    const discount = r.discounts[id] ?? 0;
    const price = techPrice(id, knownBy, discount);
    const progress = r.progress[id] ?? 0;
    const exotics = r.exoticsPaid.includes(id) ? 0 : RESEARCH.cost[def.tier].exotics;
    const command: Command = active
      ? { type: 'cancelResearch', playerId: me, tech: id }
      : { type: 'startResearch', playerId: me, tech: id };
    const blocker = known ? null : commandBlocker(state, command);
    const requires = def.requires.map((x) => `«${t(locale, `tech.${x}.name`)}»`).join(', ');
    const note = known
      ? t(locale, 'research.known')
      : active || progress > 0
        ? t(locale, 'research.progress', { done: num(locale, progress), price: num(locale, price) })
        : met
          ? exotics
            ? t(locale, 'research.priceExotics', { price: num(locale, price), exotics })
            : t(locale, 'research.price', { price: num(locale, price) })
          : def.requiresCount < def.requires.length
            ? t(locale, 'research.afterAny', { n: def.requiresCount })
            : t(locale, 'research.after', { tech: requires });
    const effects = techEffects(locale, id);
    const unlocks = techUnlocks(locale, id);
    return {
      id,
      name: t(locale, `tech.${id}.name`),
      branch: def.branch,
      tier: def.tier,
      status,
      price,
      progress,
      exotics,
      knownBy,
      discount,
      note,
      does: [
        ...effects,
        ...(unlocks.length ? [t(locale, 'research.unlocks', { list: unlocks.join(', ') })] : []),
      ],
      requires: def.requires.length
        ? def.requiresCount < def.requires.length
          ? t(locale, 'research.requiresAny', { n: def.requiresCount, list: requires })
          : t(locale, 'research.requires', { list: requires })
        : '',
      command: known || blocker ? null : command,
      reason: known || !blocker ? null : researchReason(locale, blocker, player, exotics),
    };
  });

  // A rough time to finish: the stock goes first, then each cycle's science, shared equally.
  const share = r.active.length ? perCycle / r.active.length : 0;
  let pool = stock;
  const active = r.active.map((id) => {
    const node = nodes.find((n) => n.id === id)!;
    const left = Math.max(0, node.price - node.progress);
    const fromStock = Math.min(left, pool / Math.max(1, r.active.length));
    pool -= fromStock;
    const rest = left - fromStock;
    const cycles = rest <= 1e-9 ? 1 : share > 0 ? 1 + Math.ceil(rest / share) : Infinity;
    return {
      id,
      name: node.name,
      value: node.price > 0 ? Math.min(1, node.progress / node.price) : 0,
      eta: Number.isFinite(cycles)
        ? t(locale, 'research.eta', {
            time: clock(cycles * BALANCE.cycleTicks - (state.tick % BALANCE.cycleTicks)),
          })
        : t(locale, 'research.stalled'),
    };
  });

  return {
    slots: { used: r.active.length, total: researchSlots(player.planet) },
    perCycle,
    stock,
    branches: BRANCHES.map((id) => ({ id, name: t(locale, `techBranch.${id}.name`) })),
    nodes,
    active,
    hint: hint(locale, player, perCycle),
  };
}

function hint(locale: Locale, player: PlayerState, perCycle: number): string {
  if (perCycle > 0 || player.planet.lastCycle === null) {
    return t(locale, 'research.diffusion', {
      pct: pct(1 - RESEARCH.diffusionFactor),
    });
  }
  return t(locale, 'research.noScience');
}

/** Why research cannot start, and how to clear it (#150). */
function researchReason(
  locale: Locale,
  reason: string,
  player: PlayerState,
  exotics: number,
): string {
  if (reason === 'no research slot') {
    // A research center of level 2 runs two technologies at once.
    const level = Math.max(
      0,
      ...player.planet.buildings.filter((b) => b.id === 'researchCenter').map((b) => b.level),
    );
    return t(locale, level >= 2 ? 'remedy.researchSlot' : 'remedy.researchSlotUpgrade');
  }
  if (reason === 'not enough exotics') {
    return t(locale, 'remedy.exotics', { n: Math.ceil(exotics - player.goods.exotics) });
  }
  return reasonText(locale, reason, undefined, 'research');
}
